// Modulo SESION — la puerta.
// Es la unica pantalla sin barra superior: todavia no hay usuario que mostrar.
//
// DOS ESTADOS, los del dibujo `NACH Login.dc.html`:
//
//   1 · SELECTOR DE CUENTA  para quien ya entro en ESTE equipo. Se reconoce en
//       una fila y solo pone la clave.
//   2 · CREDENCIALES        el primer ingreso, o «ingresar con otra cuenta».
//
// ⚠ LO QUE SE GUARDA, Y LO QUE NO.
//
// Las cuentas viven en el `localStorage` de ESTE navegador y en ningun otro
// sitio: el servidor no sabe ni le importa quien uso antes esta terminal. Se
// guarda el MINIMO para reconocer a alguien —nombre, correo y rol— y NUNCA la
// clave ni el token.
//
// Y cada fila se puede quitar con su ✕. Sin eso, quien dejo de trabajar ahi se
// queda en la pantalla del mostrador para siempre, y su correo a la vista de
// cualquiera que se acerque. Es una terminal de tienda, no un portatil.

import { api } from "../comun/api.js";
import { $ } from "../comun/dom.js";
import { ponerOjitos } from "../comun/ojito.js";

const LLAVE = "nachpos.cuentas";
const MAXIMO = 5;          // mas de cinco deja de ser reconocer y pasa a ser buscar

const form = $("#form-entrar");
const caja = $("#error");
const boton = $("#btn-entrar");

/** Las dos tintas del dibujo para el circulo. Sale del correo y no al azar:
 *  la misma persona tiene siempre el mismo color, que es lo que hace que la
 *  fila se reconozca antes de leerla. */
const TINTAS = ["#1e3a5f", "#1d5fa8", "#3b82c4"];
const tintaDe = (correo) => {
  let h = 5381;
  for (const c of correo) h = ((h << 5) + h + c.charCodeAt(0)) >>> 0;
  return h % TINTAS.length;
};

/** Las tintas de la lista, sin dos seguidas iguales.
 *
 *  ⚠ El color sale del CORREO y no de la posicion: la lista se reordena —la
 *  ultima cuenta usada sube— y con el color atado al sitio, cada quien
 *  cambiaria de color al entrar otro. El circulo esta para reconocer la fila
 *  antes de leerla, y eso solo funciona si no se mueve.
 *
 *  Lo que si se corrige es el choque: con tres tintas y tres cuentas, que dos
 *  caigan igual es normal —medido, tres correos de la misma empresa daban el
 *  mismo azul—. Si la fila repite la de arriba, se corre una. Sigue siendo
 *  estable mientras no cambien sus vecinas, y se ve que son personas distintas.
 */
function tintasDe(cuentas) {
  let anterior = -1;
  return cuentas.map((c) => {
    let i = tintaDe(c.correo);
    if (i === anterior) i = (i + 1) % TINTAS.length;
    anterior = i;
    return TINTAS[i];
  });
}

const inicialesDe = (nombre, correo) => {
  // ⚠ Solo letras. El nombre sale de lo que se guardo en ESTE navegador y
  // puede traer cualquier cosa: con "Jonathan <b>Florencio" las iniciales
  // salian «J<».
  const partes = (nombre || "").replace(/[^\p{L}\s]/gu, " ")
    .trim().split(/\s+/).filter(Boolean);
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase();
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (correo || "?").slice(0, 2).toUpperCase();
};

function leerCuentas() {
  try {
    const guardado = JSON.parse(localStorage.getItem(LLAVE) || "[]");
    return Array.isArray(guardado) ? guardado.filter((c) => c && c.correo) : [];
  } catch {
    // Un `localStorage` con basura no puede dejar sin entrar a nadie: se
    // ignora y la pantalla cae al formulario de siempre.
    return [];
  }
}

function guardarCuenta(usuario) {
  if (!usuario || !usuario.correo) return;
  try {
    const otras = leerCuentas().filter(
      (c) => c.correo.toLowerCase() !== usuario.correo.toLowerCase());
    // ⚠ Solo estos tres campos. Nada de claves, ni tokens, ni la empresa.
    const cuenta = {
      correo: usuario.correo,
      nombre: usuario.nombre || "",
      rol: usuario.superadmin ? "Soporte NACH POS" : (usuario.rol || ""),
    };
    localStorage.setItem(LLAVE, JSON.stringify([cuenta, ...otras].slice(0, MAXIMO)));
  } catch {
    // Sin espacio o en modo privado: entrar tiene que funcionar igual.
  }
}

function olvidarCuenta(correo) {
  try {
    localStorage.setItem(LLAVE, JSON.stringify(
      leerCuentas().filter((c) => c.correo.toLowerCase() !== correo.toLowerCase())));
  } catch { /* igual que arriba */ }
}

// ─────────────────────────────────────────────────────── los dos estados

function verCuentas() {
  const cuentas = leerCuentas();
  if (!cuentas.length) return verCredenciales();

  const tintas = tintasDe(cuentas);
  $("#cuentas").innerHTML = cuentas.map((c, i) => `
    <div class="sesion__cuenta" role="button" tabindex="0"
         data-correo="${encodeURIComponent(c.correo)}">
      <span class="sesion__cuenta__av" style="background:${tintas[i]}"
        >${inicialesDe(c.nombre, c.correo)}</span>
      <span class="sesion__cuenta__t">
        <span class="sesion__cuenta__n"></span>
        <span class="sesion__cuenta__d"></span>
      </span>
      <button type="button" class="sesion__cuenta__x" data-olvidar
              title="Quitar esta cuenta de este equipo"
              aria-label="Quitar esta cuenta de este equipo">&times;</button>
    </div>`).join("");

  // El nombre y el correo por DOM y no concatenando: vienen del navegador de
  // quien uso la terminal, y un nombre con comillas no puede romper la pagina.
  [...$("#cuentas").children].forEach((fila, i) => {
    const c = cuentas[i];
    fila.querySelector(".sesion__cuenta__n").textContent = c.nombre || c.correo;
    fila.querySelector(".sesion__cuenta__d").textContent =
      c.rol ? `${c.correo} · ${c.rol}` : c.correo;
  });

  $("#paso-cuentas").hidden = false;
  form.hidden = true;
  $("#otra-cuenta").hidden = false;
  $("#volver-cuentas").hidden = true;
}

function verCredenciales({ cuenta = null } = {}) {
  $("#paso-cuentas").hidden = true;
  form.hidden = false;
  caja.hidden = true;

  // Con cuenta elegida, el correo ya esta puesto y no se vuelve a teclear: lo
  // que se ve es de QUIEN es la clave que se va a poner.
  $("#quien").hidden = !cuenta;
  $("#campo-correo").hidden = Boolean(cuenta);
  if (cuenta) {
    $("#correo").value = cuenta.correo;
    $("#quien-av").textContent = inicialesDe(cuenta.nombre, cuenta.correo);
    $("#quien-av").style.background = TINTAS[tintaDe(cuenta.correo)];
    $("#quien-n").textContent = cuenta.nombre || cuenta.correo;
    $("#quien-d").textContent = cuenta.rol ? `${cuenta.correo} · ${cuenta.rol}`
                                           : cuenta.correo;
  } else {
    $("#correo").value = "";
  }

  const hayGuardadas = leerCuentas().length > 0;
  $("#otra-cuenta").hidden = true;
  $("#volver-cuentas").hidden = !hayGuardadas;

  // ⚠ El foco va donde HAY QUE ESCRIBIR. Con cuenta elegida el correo ya esta
  // puesto y solo falta la clave; sin cuenta, el campo vacio es el correo. La
  // version anterior mandaba el foco a la clave con el correo en blanco.
  (cuenta ? $("#clave") : $("#correo")).focus();
}

// ─────────────────────────────────────────────────────────────── entrar

function mostrarError(mensaje) {
  // El mensaje viene del servidor ya escrito en espanol para una vendedora.
  // No se reescribe aca ni se muestra el codigo tecnico.
  caja.textContent = mensaje;
  caja.hidden = false;
}

async function entrar(evento) {
  evento.preventDefault();
  caja.hidden = true;
  boton.disabled = true;
  boton.textContent = "Entrando...";

  try {
    const datos = await api.post("/sesion/entrar", {
      // El correo decide la empresa: ver `nachpos/servicios/registro_clientes.py`.
      correo: $("#correo").value,
      clave: $("#clave").value,
      siguiente: $("#siguiente").value,
    });
    // ⚠ Se guarda DESPUES de que el servidor diga que si. Guardarlo al escribir
    // dejaria en la terminal correos que nunca fueron de nadie — incluido el de
    // quien se equivoco de tecla.
    guardarCuenta(datos.usuario);
    // Recarga completa a proposito: la barra superior y los permisos se arman
    // en el servidor, asi que la pagina siguiente tiene que pedirse de nuevo.
    window.location.assign(datos.siguiente);
  } catch (e) {
    mostrarError(e.message);
    $("#clave").value = "";
    $("#clave").focus();
    boton.disabled = false;
    boton.textContent = "Ingresar";
  }
}

// ──────────────────────────────────────────────────────────────── inicio

form.addEventListener("submit", entrar);

$("#cuentas").addEventListener("click", (e) => {
  const fila = e.target.closest("[data-correo]");
  if (!fila) return;
  const correo = decodeURIComponent(fila.dataset.correo);

  if (e.target.closest("[data-olvidar]")) {
    olvidarCuenta(correo);
    verCuentas();
    return;
  }
  verCredenciales({ cuenta: leerCuentas().find((c) => c.correo === correo) });
});

// Las filas son divs con `role="button"`: sin esto no se pueden usar con
// teclado, y el lector de pantalla las anuncia como boton y no responden.
$("#cuentas").addEventListener("keydown", (e) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  const fila = e.target.closest("[data-correo]");
  if (!fila) return;
  e.preventDefault();
  fila.click();
});

$("#otra-cuenta").addEventListener("click", (e) => {
  e.preventDefault();
  verCredenciales();
});

$("#volver-cuentas").addEventListener("click", (e) => {
  e.preventDefault();
  verCuentas();
});

verCuentas();

// El ojito de ver la clave, en todos los campos de esta pantalla.
ponerOjitos();
